import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MessageSquare, RefreshCw, Search, Send, Settings2, Bell, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import WhatsAppNotifications from "@/pages/dashboard/WhatsAppNotifications";
import WhatsAppDirectConnection from "@/pages/dashboard/WhatsAppDirectConnection";

const previewText = (message) => {
  const content = message?.content || {};
  if (content.type === "text") return content.text || "";
  const labels = {
    image: "📷 Imagen", audio: "🎙️ Audio", video: "🎥 Video",
    document: "📎 Documento", location: "📍 Ubicación",
    sticker: "Sticker", interactive: "Respuesta interactiva", reaction: "Reacción",
  };
  return labels[content.type] || "Mensaje";
};

function ConversationsPane() {
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [thread, setThread] = useState(null);
  const [search, setSearch] = useState("");
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const searchRef = useRef("");
  const conversationRequestRef = useRef(0);

  const loadConversations = useCallback(async (term = searchRef.current) => {
    const normalized = String(term || "").trim();
    const requestId = ++conversationRequestRef.current;
    const { data } = await api.get("/api/whatsapp/inbox/conversations", {
      params: normalized ? { search: normalized } : {},
    });
    if (requestId === conversationRequestRef.current) {
      setConversations(Array.isArray(data) ? data : []);
    }
  }, []);

  const loadThread = useCallback(async (id, markRead = false) => {
    if (!id) return;
    const { data } = await api.get(`/api/whatsapp/inbox/conversations/${id}/messages`);
    setThread(data);
    if (markRead) {
      await api.post(`/api/whatsapp/inbox/conversations/${id}/read`);
      await loadConversations(searchRef.current);
    }
  }, [loadConversations]);

  useEffect(() => {
    searchRef.current = search;
    const timer = window.setTimeout(() => {
      loadConversations(search).catch((e) => setNotice(formatApiError(e)));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [search, loadConversations]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      loadConversations(searchRef.current).catch(() => {});
      if (activeId) loadThread(activeId, false).catch(() => {});
    }, 8000);
    return () => window.clearInterval(timer);
  }, [activeId, loadConversations, loadThread]);

  const selectConversation = async (id) => {
    setActiveId(id);
    setNotice("");
    try { await loadThread(id, true); }
    catch (e) { setNotice(formatApiError(e)); }
  };

  const sendReply = async () => {
    if (!activeId || !reply.trim()) return;
    setBusy(true); setNotice("");
    try {
      await api.post(`/api/whatsapp/inbox/conversations/${activeId}/reply`, { text: reply.trim() });
      setReply("");
      await loadThread(activeId, false);
      await loadConversations(searchRef.current);
    } catch (e) { setNotice(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const active = thread?.conversation;
  return <div className="space-y-4">
    {notice && <div className="kd9-card p-3 text-sm">{notice}</div>}
    <div className="grid min-h-[62vh] gap-4 lg:grid-cols-[340px_1fr]">
      <section className="kd9-card overflow-hidden">
        <div className="border-b p-3" style={{borderColor:"var(--kd9-border)"}}>
          <div className="flex gap-2">
            <div className="relative flex-1 min-w-0">
              <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 opacity-60"/>
              <input
                type="search"
                autoComplete="off"
                aria-label="Buscar conversaciones por nombre, teléfono o ID KD9"
                className="kd9-input w-full"
                style={{ paddingLeft: "2.5rem", paddingRight: "0.75rem" }}
                placeholder="Nombre, teléfono o ID KD9"
                value={search}
                onChange={(e)=>setSearch(e.target.value)}
                onKeyDown={(e)=>{ if(e.key==="Enter"){ e.preventDefault(); loadConversations(search); } }}
              />
            </div>
            <button className="kd9-btn-outline min-w-11 rounded-xl flex items-center justify-center" onClick={()=>loadConversations(search)} aria-label="Actualizar búsqueda"><RefreshCw size={15}/></button>
          </div>
        </div>
        <div className="max-h-[58vh] overflow-auto">
          {conversations.length===0 && <div className="p-6 text-center text-sm opacity-60">Aún no hay conversaciones entrantes.</div>}
          {conversations.map((c)=><button key={c.id} onClick={()=>selectConversation(c.id)}
            className="w-full border-b p-4 text-left transition-opacity hover:opacity-90"
            style={{borderColor:"var(--kd9-border)",background:activeId===c.id?"var(--kd9-accent-soft)":"transparent"}}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0"><div className="truncate font-semibold">{c.user_name || c.profile_name || c.phone}</div>
                <div className="mt-0.5 text-[11px] opacity-60">{c.kd9_id || c.phone}</div></div>
              {!!c.unread_count && <span className="rounded-full px-2 py-0.5 text-xs font-semibold" style={{background:"var(--kd9-accent)",color:"var(--kd9-bg)"}}>{c.unread_count}</span>}
            </div>
            <div className="mt-2 truncate text-sm opacity-75">{c.last_message || "Sin vista previa"}</div>
            <div className="mt-1 text-[10px] opacity-50">{c.last_message_at ? new Date(c.last_message_at).toLocaleString("es-CO") : ""}</div>
          </button>)}
        </div>
      </section>

      <section className="kd9-card flex min-h-[62vh] flex-col overflow-hidden">
        {!active ? <div className="m-auto px-6 text-center opacity-60"><MessageSquare className="mx-auto mb-3"/><p>Selecciona una conversación para leer y responder desde KD9.</p></div> : <>
          <header className="border-b p-4" style={{borderColor:"var(--kd9-border)"}}>
            <div className="font-semibold">{active.user_name || active.profile_name || active.phone}</div>
            <div className="mt-1 text-xs opacity-60">{active.kd9_id ? `${active.kd9_id} · ` : ""}{active.phone}</div>
          </header>
          <div className="flex-1 space-y-3 overflow-auto p-4">
            {(thread.messages || []).map((m)=><div key={m.id} className={`flex ${m.direction==="outbound"?"justify-end":"justify-start"}`}>
              <div className="max-w-[82%] rounded-2xl border px-4 py-3 text-sm" style={{
                borderColor:"var(--kd9-border)",
                background:m.direction==="outbound"?"var(--kd9-accent-soft)":"var(--kd9-bg-2)"
              }}>
                <div className="whitespace-pre-wrap break-words">{previewText(m)}</div>
                <div className="mt-1 text-[10px] opacity-50">{new Date(m.created_at).toLocaleString("es-CO")}{m.direction==="outbound"&&m.status?` · ${m.status}`:""}</div>
              </div>
            </div>)}
          </div>
          <div className="border-t p-3" style={{borderColor:"var(--kd9-border)"}}>
            <div className="flex gap-2">
              <textarea className="kd9-input min-h-12 flex-1 resize-none" maxLength={4000} placeholder="Responder por WhatsApp…"
                value={reply} onChange={(e)=>setReply(e.target.value)}
                onKeyDown={(e)=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendReply();}}}/>
              <button className="kd9-btn-primary min-w-12 rounded-xl" disabled={busy||!reply.trim()} onClick={sendReply} aria-label="Enviar"><Send size={16}/></button>
            </div>
            <div className="mt-2 text-[10px] opacity-50">La respuesta sale por la sesión de WhatsApp conectada y queda registrada en KD9.</div>
          </div>
        </>}
      </section>
    </div>
  </div>;
}

function AccessPane() {
  const [settings, setSettings] = useState(null);
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async()=>{
    const {data}=await api.get("/api/director/whatsapp/inbox/access-settings");
    setSettings(data);
  },[]);
  useEffect(()=>{load().catch(e=>setNotice(formatApiError(e)));},[load]);

  const save = async(next) => {
    setBusy(true); setNotice("");
    try {
      const payload={admin_enabled:!!next.admin_enabled,support_enabled:!!next.support_enabled};
      const {data}=await api.put("/api/director/whatsapp/inbox/access-settings",payload);
      setSettings(data); setNotice("Accesos del Centro WhatsApp actualizados y auditados.");
    } catch(e){setNotice(formatApiError(e));}
    finally{setBusy(false);}
  };
  if(!settings) return <div className="kd9-card p-6">{notice || "Cargando accesos…"}</div>;
  const rows=[
    ["Administrador","admin_enabled","Puede leer y responder conversaciones cuando Dirección lo habilite."],
    ["Soporte Técnico","support_enabled","Puede atender casos y responder WhatsApp cuando Dirección lo habilite."],
  ];
  return <div className="space-y-4">
    {notice&&<div className="kd9-card p-3 text-sm">{notice}</div>}
    <div className="kd9-card p-5">
      <div className="flex items-center gap-2"><ShieldCheck size={18}/><h2 className="font-cinzel text-lg">Gobierno de acceso</h2></div>
      <p className="mt-2 text-sm opacity-65">Dirección conserva acceso permanente. Los demás roles pueden activarse o revocarse independientemente.</p>
      <div className="mt-5 space-y-3">{rows.map(([label,key,desc])=><div key={key} className="flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center sm:justify-between" style={{borderColor:"var(--kd9-border)"}}>
        <div><div className="font-semibold">{label}</div><div className="mt-1 text-xs opacity-60">{desc}</div></div>
        <button disabled={busy} onClick={()=>save({...settings,[key]:!settings[key]})}
          className={`min-w-28 rounded-xl px-4 py-2 text-sm font-semibold ${settings[key]?"kd9-btn-primary":"kd9-btn-outline"}`}>
          {settings[key]?"Habilitado":"Deshabilitado"}
        </button>
      </div>)}</div>
    </div>
  </div>;
}

export default function WhatsAppCenter(){
  const {user}=useAuth();
  const isDirector=(user?.roles||[]).includes("director");
  const [access,setAccess]=useState(null);
  const [tab,setTab]=useState("conversations");
  const [error,setError]=useState("");

  useEffect(()=>{api.get("/api/whatsapp/inbox/access").then(({data})=>setAccess(data)).catch(e=>setError(formatApiError(e)));},[]);
  const tabs=useMemo(()=>isDirector?[
    ["conversations","Conversaciones",MessageSquare],
    ["connection","Conexión",ShieldCheck],
    ["access","Accesos",Settings2],
    ["notifications","Notificaciones",Bell],
  ]:[["conversations","Conversaciones",MessageSquare]],[isDirector]);

  if(error) return <div className="kd9-card p-6">{error}</div>;
  if(!access) return <div className="kd9-card p-6">Cargando Centro WhatsApp KD9…</div>;
  if(!access.allowed) return <div className="kd9-card p-6"><h1 className="font-cinzel text-xl">Centro WhatsApp KD9</h1><p className="mt-3 text-sm opacity-70">Dirección no ha habilitado este módulo para tu rol.</p></div>;

  return <div className="space-y-5" data-testid="whatsapp-center">
    <header><div className="text-xs uppercase tracking-[.3em] kd9-gold-text">KD9 · Centro WhatsApp</div>
      <h1 className="mt-2 font-cinzel text-2xl md:text-3xl">Centro WhatsApp KD9</h1>
      <p className="mt-2 text-sm opacity-65">Lee y responde conversaciones desde KD9 sin perder el historial del canal.</p></header>
    <div className="flex flex-wrap gap-2">{tabs.map(([key,label,Icon])=><button key={key} onClick={()=>setTab(key)}
      className={`rounded-xl px-4 py-2 text-sm ${tab===key?"kd9-btn-primary":"kd9-btn-outline"}`}><Icon size={15} className="mr-2 inline"/>{label}</button>)}</div>
    {tab==="conversations"&&<ConversationsPane/>}
    {isDirector&&tab==="connection"&&<WhatsAppDirectConnection/>}
    {isDirector&&tab==="access"&&<AccessPane/>}
    {isDirector&&tab==="notifications"&&<WhatsAppNotifications/>}
  </div>;
}
